import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./Blog.css";
import { blogCategories, blogPosts } from "../data/blogPosts";

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedPost, setLocalSelectedPost] = useState(null);
  const postId = searchParams.get("post");
  const selectedPost = useMemo(
    () => (postId ? blogPosts.find((post) => String(post.id) === postId) : localSelectedPost),
    [postId, localSelectedPost]
  );

  const openPost = (post) => {
    setLocalSelectedPost(post);
    setSearchParams({ post: String(post.id) });
  };

  const closePost = () => {
    setLocalSelectedPost(null);
    setSearchParams({});
  };

  const filtered = blogPosts.filter((post) => {
    const matchesCategory = activeCategory === "All" || post.category === activeCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // ── Article Detail View ──────────────────────────────────────────
  if (selectedPost) {
    return (
      <div className="blog-page">
        <div className="article-hero" style={{ backgroundImage: `url(${selectedPost.image})` }}>
          <div className="article-hero-overlay">
            <div className="blog-container">
              <button className="article-back-btn" onClick={closePost}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Back to Blog
              </button>
              <span className="blog-card-category" style={{ position: "static", display: "inline-block", marginBottom: "16px" }}>
                {selectedPost.category}
              </span>
              <h1 className="article-title">{selectedPost.title}</h1>
              <div className="blog-card-meta" style={{ color: "rgba(255,255,255,0.8)" }}>
                <span>{selectedPost.date}</span>
                <span className="blog-card-dot">·</span>
                <span>{selectedPost.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="article-body-wrap">
          <div className="blog-container">
            <div className="article-content">
              {selectedPost.content.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            <div className="article-footer">
              <button className="article-back-btn-bottom" onClick={closePost}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Back to all articles
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Blog List View ───────────────────────────────────────────────
  return (
    <div className="blog-page">
      {/* Hero */}
      <section className="blog-hero">
        <div className="blog-hero-content">
          <span className="blog-eyebrow">SOLACE NEURO</span>
          <h1>Insights & Resources</h1>
          <p>
            Expert perspectives on mental health, child development, and neurological care —
            written to inform and empower families across Kerala.
          </p>
          <div className="blog-search-wrap">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search articles…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="blog-filters">
        <div className="blog-container">
          <div className="filter-pills">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                className={`filter-pill ${activeCategory === cat ? "active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="blog-posts-section">
        <div className="blog-container">
          {filtered.length === 0 ? (
            <div className="blog-empty">
              <p>No articles found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="blog-grid">
              {filtered.map((post, index) => (
                <article
                  key={post.id}
                  className={`blog-card ${index === 0 && activeCategory === "All" && !searchQuery ? "featured" : ""}`}
                >
                  <div className="blog-card-image">
                    <img src={post.image} alt={post.title} />
                    <span className="blog-card-category">{post.category}</span>
                  </div>
                  <div className="blog-card-body">
                    <div className="blog-card-meta">
                      <span className="blog-card-date">{post.date}</span>
                      <span className="blog-card-dot">·</span>
                      <span className="blog-card-read">{post.readTime}</span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <button className="blog-card-link" onClick={() => openPost(post)}>
                      Read article
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="blog-newsletter">
        <div className="blog-container">
          <div className="newsletter-box">
            <h3>Stay informed</h3>
            <p>Receive our latest articles and mental health resources directly in your inbox.</p>
            <div className="newsletter-form">
              {/* <input type="email" placeholder="Your email address" className="newsletter-input" /> */}
              <input
                type="email"
                placeholder="Your email address"
                className="newsletter-input"
                autoComplete="off"
              />
              <button className="newsletter-btn">Subscribe</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
